<template>
  <div>
    <popup />
    <flash-message />
    <div class="row">
      <div class="col-sm-8 col-xs-12">
        <panel class="remove-margin">
          <div slot="title">
            <div class="col-xs-12">Find a company</div>
          </div>
          <div slot="body">
            <div class="row">
              <div class="col-xs-9 col-sm-7">
                <div class="input-group input-group-md">
                  <span class="input-group-addon" id="sizing-addon1">
                    <i class="glyphicon glyphicon-search"></i>
                  </span>
                  <input
                    type="search"
                    v-on:keyup.13="performSearch"
                    v-on:keyup="searchTimeOut"
                    v-model="searchTerm"
                    name="search"
                    class="form-control"
                    id="search"
                    placeholder="Enter a company name, registration number or a postcode"
                    autocomplete="off"
                  />
                  <span class="input-group-addon busy-feedback"
                    ><img src="/images/ajax-loader.gif" v-show="showBusy"
                  /></span>
                </div>
              </div>
              <div class="col-xs-3 col-sm-2">
                <select
                  class="form-control mr-sm-2"
                  id="inlineFormCustomSelectPref"
                  style="padding-right: 0; padding-left: 0"
                  v-model="selectedCountry"
                >
                  <option value="gb">UK</option>
                  <option value="ie">IE</option>
                  <option value="all">UK & IE</option>
                </select>
              </div>
              <div class="col-xs-12 col-sm-3 text-center">
                <button
                  class="btn btn-primary btn-block search-button"
                  v-on:click.prevent="performSearch"
                  type="submit"
                  :disabled="searchTerm.length < 2"
                >
                  Search
                </button>
              </div>
            </div>
            <div class="row">
              <div class="col-xs-12 col-sm-10">
                <span class="text-danger field-validation-error search-hint">{{ error }}</span>
              </div>
            </div>
            <div class="row">
              <div class="col-xs-12">
                <div class="checkbox">
                  <label for="includeNonActive">
                    <input id="includeNonActive" type="checkbox" v-model="includeNonActive" />
                    &nbsp;Include companies that are not actively trading.
                  </label>
                </div>
              </div>
            </div>
            <div v-if="resultsFound" class="search-results">
              <div v-for="result of searchResults" :key="result.id" class="search-result">
                <div class="row">
                  <div class="col-xs-9 company-name">{{ result.name }}</div>
                  <div class="col-xs-3">
                    <a
                      v-if="result.queue_status === ''"
                      v-on:click.prevent="requestReport(result)"
                      role="button"
                      class="btn btn-runReport btn-companiesSearch btn-xs pull-right"
                    >
                      run report &gt;
                    </a>
                    <router-link
                      v-else-if="result.queue_status === 'processed'"
                      :to="{ path: '/reports/' + result.report_id }"
                    >
                      <a
                        v-on:click.prevent=""
                        role="button"
                        class="btn btn-viewReport btn-xs pull-right"
                        >View report &gt;</a
                      >
                    </router-link>
                    <a
                      v-else-if="result.queue_status === 'processing'"
                      role="button"
                      disabled
                      class="btn btn-viewReport btn-companiesSearch btn-xs pull-right"
                      >pending
                    </a>
                  </div>
                </div>
                <div class="row">
                  <div class="col-xs-6 company-registration">{{ result.registration_number }}</div>
                </div>
                <div class="row result-address" v-if="result.address !== ''">
                  <div class="col-xs-8">{{ result.address }}</div>
                </div>
                <div class="row" v-if="companyInactive(result.company_status)">
                  <div class="col-xs-8">
                    <span class="label label-inactive">{{ result.company_status }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="search-results search-result">
              <div class="row">
                <div class="col-xs-12 company-name no-results">
                  <template v-if="!hasSearched">
                    <h5>Search Tips</h5>
                    <p>
                      You can search for a company using its name, registration number or postcode
                      using the search field above.
                    </p>
                    <p>
                      EndCustomer's search engine uses the following rules to determine which search
                      to apply for you
                    </p>
                    <ul>
                      <li>
                        If you type 2 or 3 characters, EndCustomer will search by postcode only.
                      </li>
                      <li>
                        If you type upto 8 characters, EndCustomer will search by all three
                        criteria.
                      </li>
                      <li>
                        If you type more than 8 characters, EndCustomer will only search by name and
                        registration number.
                      </li>
                    </ul>
                  </template>
                  <template v-else>
                    Sorry, no results found for your search. Try a variation of the name or use the
                    company's registration number.
                  </template>
                </div>
              </div>
            </div>
          </div>
        </panel>
      </div>
      <div class="col-sm-4 col-xs-12">
        <processing-reports ref="processingReports" @report-processed="onReportProcessed" />
        <recent-reports ref="recentReports" />
      </div>
    </div>
  </div>
</template>

<script>
import Panel from '@/components/app/Panel';
import ProcessingReports from '@/components/reports/processing/ProcessingReports';
import RecentReports from '@/components/reports/recent/RecentReports';
import FlashMessage from '@/components/tools/FlashMessage';
import Popup from '@/components/tools/Popup';
import CompanyMixin from '@/mixins/Company';
import Metrics from '@/mixins/Metrics';
import Axios from '@/packages/axios/Axios';

export default {
  components: {
    FlashMessage,
    Panel,
    Popup,
    ProcessingReports,
    RecentReports,
  },
  created() {
    this.selectedCountry = window.localStorage.getItem('companySearchCountry') || 'gb';
  },
  data() {
    return {
      includeNonActive: false,
      searchTerm: '',
      searchResults: [],
      reportId: '',
      reportProcessing: false,
      processingInterval: 5000,
      selectedCountry: 'gb',
      showBusy: false,
      resultsFound: false,
      pristine: true,
      hasSearched: false,
      error: '',
    };
  },
  methods: {
    onReportProcessed() {
      this.$refs.recentReports.refresh();
    },
    searchTimeOut() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
        this.showBusy = false;
      }
      this.timer = setTimeout(() => {
        this.performSearch();
      }, 800);
    },
    transformSelectedCountryToString() {
      if (this.selectedCountry === 'all') {
        return 'gb,ie';
      }
      return this.selectedCountry;
    },
    performSearch() {
      if (this.searchTerm.length < 2) {
        return;
      }
      const countries = this.transformSelectedCountryToString();
      this.dataHasLoaded = false;
      this.showBusy = true;
      Axios.get('/v1/companies/search', {
        params: {
          q: this.searchTerm,
          i: this.includeNonActive,
          c: countries,
          source: 2,
          page: 1,
          per_page: 12,
        },
      })
        .then((response) => {
          this.searchResults = response.data;
          if (response.data.length === 0) {
            this.resultsFound = false;
          } else {
            this.resultsFound = true;
          }
          this.dataHasLoaded = true;
          this.showBusy = false;
          this.hasSearched = true;
        })
        .catch((e) => {
          if (e.response) {
            // this.errors.push(e);
            this.searchResults = [];
            this.dataHasLoaded = true;
            this.showBusy = false;
            this.resultsFound = false;

            switch (e.response.status) {
              case 401:
                this.$router.push('/error/401');
                break;
              case 403:
                this.$router.push('/error/403');
                break;
              case 404:
                this.$router.push('/error/404');
                break;
              default:
                this.$router.push('/error/unknown');
            }
          }
        });
    },
    requestReport(searchResult) {
      this.$eventBus.$emit('show-popup', 'Adding report to your queue');
      this.showBusy = false;
      this.reportId = null;
      this.dataHasLoaded = false;
      this.reportProcessing = true;
      Axios.post('/v1/reports/' + searchResult.id)
        .then((response) => {
          if (response.data.company !== undefined) {
            this.$store.commit('SHOW_FLASH', {
              body:
                `${
                  "We couldn't find the company you were looking for. However, we have found a company " +
                  'matching its name exactly ('
                }${response.data.company.name}) with a different ` +
                `registration number (${response.data.company.registration_number}) and have run your ` +
                'report on the matching business instead.',
              duration: 60 /* <== This can be made shorter, for sure ;-) */,
              type: 'info',
              title: 'Alternative report',
            });
            this.reportProcessing = false;
            this.reportId = response.data.report_id;
          } else if (response.data.list !== undefined) {
            this.$store.commit('SHOW_FLASH', {
              body: 'Our data provider has no detailed information on the company you requested.',
              duration: 60 /* <== This can be made shorter, for sure ;-) */,
              type: 'error',
              title: 'Problem running your report',
            });
            this.reportProcessing = false;
            this.reportId = undefined;
          } else {
            this.reportProcessing = false;
            this.reportId = response.data;
            this.writeMetric('interaction', {
              event: 'new-report-requested',
              report_id: this.reportId,
              parent_report_id: null,
            });
            this.$refs.processingReports.addReport({
              id: response.data,
              company_name: searchResult.name,
              registration_number: searchResult.registration_number,
              percent_complete: 0,
              status: 'queued',
            });
          }
        })
        .catch((e) => {
          if (e.response) {
            // this.errors.push(e);
            this.showBusy = false;

            switch (e.response.status) {
              case 401:
                this.$router.push('/error/401');
                break;
              case 403:
                this.$router.push('/error/403');
                break;
              case 404:
                this.$router.push('/error/404');
                break;
              default:
                this.$router.push('/error/unknown');
            }
          }
        });
    },
    companyInactive(status) {
      if (status === 'Active' || status === 'Open' || status === 'Closed') {
        return false;
      }
      return true;
    },
  },
  mixins: [CompanyMixin, Metrics],
  watch: {
    selectedCountry(newValue) {
      window.localStorage.setItem('companySearchCountry', newValue);
    },
    searchTerm(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.hasSearched = false;
      }
      if (newValue.length === 0) {
        this.pristine = true;
        this.error = '';
      } else if (newValue.length < 2) {
        this.pristine = false;
        this.error = 'Keep typing... we need at least 2 characters to search';
      } else {
        this.pristine = false;
        this.error = '';
      }
    },
  },
};
</script>

<style scoped>
.search-results {
  margin-top: 10px;
}

.search-result {
  padding: 8px;
  background-color: #ffffff;
  border-color: #efefef;
  border-width: 1px;
  border-style: solid;
  margin-bottom: 5px;
}

.search-result:hover {
  background-color: #f8f8f8;
}


.remove-margin {
  margin-bottom: 0px;
}

.company-registration {
  font-size: 11px;
  color: rgb(109, 109, 109);
}

.search-button {
  margin-top: 10px;
  margin-bottom: 10px;
}

@media only screen and (min-width: 768px) {
  .search-button {
    margin-top: 0px;
    margin-bottom: 0px;
  }

  .company-name {
    font-size: 16px;
  }

  .company-registration {
    font-size: 14px;
  }
}

.result-address {
  font-size: 12px;
  color: rgb(134, 134, 134);
}

.label-inactive {
  background-color: #fa9c9c;
}

.search-hint {
  font-weight: 400;
  font-size: 12px;
}

.busy-feedback {
  width: 42px;
}

.no-results {
  color: rgb(167, 120, 21);
  font-size: 1rem;
  padding-left: 40px;
  padding-right: 40px;
}

.no-results h5 {
  color: #a77815 !important;
}
</style>
